/**
 * 送信前の音声変換（A 案・アップロード高速化）。
 *
 * 背景: バックエンドは受領音声を必ず 16kHz モノラルへ落としてから Whisper/pyannote に渡す
 * （audio_preprocessing.py: target_sample_rate=16000）。よって高音質のまま送るのは無駄で、
 * 上り帯域が細い環境ほどアップロードが遅い。ブラウザ側で事前に 16kHz モノラル MP3 へ変換し、
 * 送信バイト数を大幅に削減する（書き起こし品質は無劣化＝どうせ 16kHz に落ちるため）。
 *
 * 構成: decode→16kHz モノラルへリサンプル（OfflineAudioContext・メインスレッド／高速）→
 * 16bit PCM 化 → MP3 エンコードは Worker（UI を塞がない）。decode 不可など失敗時は呼び出し側で
 * 元ファイル送信にフォールバックする。
 *
 * 適用範囲: この変換は音声長に比例したメモリを要求する（decodeAudioData が入力全体を
 * 非圧縮 PCM に展開するため）。長時間ファイルではブラウザのメモリ上限を超え、タブごと
 * "Out of Memory" で落ちて try/catch のフォールバックすら効かない。よって変換前に
 * shouldTranscode でピークメモリを見積もり、予算内のファイルだけ変換する。
 * 予算超過分は原本のまま送信され、サーバ側 ffmpeg 前処理が音声を抽出する（結果は同じで、
 * アップロードのバイト数だけが増える）。恒久対応は WebCodecs によるチャンクデコードへの移行。
 */
const TARGET_SAMPLE_RATE = 16000; // Whisper 推奨。backend と一致
const TARGET_CHANNELS = 1; // モノラル
const TARGET_KBPS = 64; // 16kHz モノ音声に十分（音声明瞭・~28MB/時）

/**
 * 変換に許すピークメモリ。Chrome は 1 タブあたり数GB で確保に失敗し、TypedArray 1本あたりの
 * 上限も約2GBのため、安全側に 2GB を上限とする（48kHz ステレオでおよそ1時間相当）。
 */
const TRANSCODE_MEMORY_BUDGET_BYTES = 2 * 1024 * 1024 * 1024;
/** 音声長を判定できなかった場合に変換を許すサイズ上限（見積れない以上、安全側に倒す）。 */
const UNKNOWN_DURATION_MAX_BYTES = 200 * 1024 * 1024;
/** decodeAudioData はソースのチャンネル数を保つ。事前に判らないためステレオ（最悪値）を仮定する。 */
const ASSUMED_SOURCE_CHANNELS = 2;
/** AudioContext を作れない環境での既定サンプルレート（一般的な実機値）。 */
const FALLBACK_SAMPLE_RATE = 48000;
/** メタデータ読み込みの待ち上限。超えたら「判定不能」として扱う。 */
const DURATION_PROBE_TIMEOUT_MS = 3000;

/** AudioContext のコンストラクタを取得する（Safari は prefix つき）。 */
function getAudioContextCtor(): typeof AudioContext {
  return (
    window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
  );
}

/**
 * decodeAudioData の展開先サンプルレート（＝実機のレート）を得る。
 * 取得できない環境では既定値を返す（見積りが外れても変換を試すだけで、失敗時は原本送信）。
 */
function detectDeviceSampleRate(): number {
  try {
    const ctx = new (getAudioContextCtor())();
    const rate = ctx.sampleRate;
    void ctx.close();
    return Number.isFinite(rate) && rate > 0 ? rate : FALLBACK_SAMPLE_RATE;
  } catch {
    return FALLBACK_SAMPLE_RATE;
  }
}

/**
 * メタデータだけを読んで音声長（秒）を得る。デコードは伴わないためメモリを食わない。
 * 対応外コーデック・タイムアウトなど判定できない場合は null を返す。
 */
export async function probeDurationSec(file: File): Promise<number | null> {
  let url: string;
  try {
    url = URL.createObjectURL(file);
  } catch {
    return null;
  }
  const media = document.createElement('audio');
  try {
    return await new Promise<number | null>((resolve) => {
      const finish = (value: number | null) => {
        clearTimeout(timer);
        resolve(value);
      };
      const timer = setTimeout(() => finish(null), DURATION_PROBE_TIMEOUT_MS);
      media.preload = 'metadata';
      media.onloadedmetadata = () => finish(Number.isFinite(media.duration) ? media.duration : null);
      media.onerror = () => finish(null);
      media.src = url;
    });
  } finally {
    media.removeAttribute('src');
    URL.revokeObjectURL(url);
  }
}

/**
 * transcodeTo16kMonoMp3 のピークメモリ（バイト）を見積もる。
 *
 * 同時に生存する確保は次の4つ。decodeAudioData の展開が支配的で、48kHz ステレオなら
 * 約1.4GB/時に達する（＝これが長時間ファイルで Out of Memory になる主因）。
 */
export function estimateTranscodePeakBytes(
  durationSec: number,
  sampleRate: number,
  fileSizeBytes: number,
): number {
  const decoded = durationSec * sampleRate * ASSUMED_SOURCE_CHANNELS * 4; // AudioBuffer（Float32）
  const rendered = durationSec * TARGET_SAMPLE_RATE * TARGET_CHANNELS * 4; // OfflineAudioContext 出力（Float32）
  const quantized = durationSec * TARGET_SAMPLE_RATE * TARGET_CHANNELS * 2; // Int16 PCM
  return fileSizeBytes + decoded + rendered + quantized;
}

/**
 * 送信前変換を行ってよいか判定する（純関数。durationSec が null なら判定不能）。
 * false のときは変換せず原本を送る。
 */
export function shouldTranscode(
  durationSec: number | null,
  sampleRate: number,
  fileSizeBytes: number,
): boolean {
  if (durationSec === null) return fileSizeBytes <= UNKNOWN_DURATION_MAX_BYTES;
  return estimateTranscodePeakBytes(durationSec, sampleRate, fileSizeBytes) <= TRANSCODE_MEMORY_BUDGET_BYTES;
}

/**
 * ファイルが送信前変換のメモリ予算に収まるかを判定する（呼び出し側は false なら原本を送る）。
 */
export async function canTranscodeWithinMemory(file: File): Promise<boolean> {
  return shouldTranscode(await probeDurationSec(file), detectDeviceSampleRate(), file.size);
}

/** 拡張子を .mp3 へ付け替える（無拡張子なら付与、ベース名が空なら audio）。 */
export function rewriteToMp3Filename(name: string): string {
  const base = name.replace(/\.[^./\\]+$/, '');
  return `${base.trim() || 'audio'}.mp3`;
}

/** Float32 [-1,1] を 16bit PCM（Int16）へ量子化する（クリップ保護つき）。 */
export function floatTo16BitPCM(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

/** 入力音声を decode し 16kHz モノラルの Float32 サンプルへ変換する。 */
async function decodeToMono16k(file: File): Promise<Float32Array> {
  const arrayBuffer = await file.arrayBuffer();

  // decode は実機サンプリングレートで行う（OfflineAudioContext でレート変換は後段に任せる）
  const decodeCtx = new (getAudioContextCtor())();
  let decoded: AudioBuffer;
  try {
    // decodeAudioData は渡した ArrayBuffer を detach するが、以降 arrayBuffer を参照しないため
    // コピーは不要。大容量（数百MB）ではコピー分がそのままピークメモリの上乗せになり、
    // decode 失敗（"Unable to decode audio data"）を招くので原本をそのまま渡す。
    decoded = await decodeCtx.decodeAudioData(arrayBuffer);
  } finally {
    void decodeCtx.close();
  }

  // 16kHz・モノラルでレンダリング（複数チャンネルは destination が 1ch のため自動ダウンミックス）
  const frames = Math.max(1, Math.ceil(decoded.duration * TARGET_SAMPLE_RATE));
  const offline = new OfflineAudioContext(TARGET_CHANNELS, frames, TARGET_SAMPLE_RATE);
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.connect(offline.destination);
  source.start();
  const rendered = await offline.startRendering();
  return rendered.getChannelData(0);
}

/**
 * 音声ファイルを 16kHz モノラル MP3 の File へ変換する。
 *
 * onProgress は変換進捗（0〜100）を通知する。失敗時は例外を送出するため、呼び出し側で
 * try/catch して元ファイル送信へフォールバックすること。
 */
export async function transcodeTo16kMonoMp3(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<File> {
  const mono = await decodeToMono16k(file);
  const pcm = floatTo16BitPCM(mono);

  const worker = new Worker(new URL('../workers/mp3Encoder.worker.ts', import.meta.url), {
    type: 'module',
  });
  try {
    const mp3 = await new Promise<Uint8Array>((resolve, reject) => {
      worker.onmessage = (e: MessageEvent) => {
        const data = e.data as
          | { type: 'progress'; percent: number }
          | { type: 'done'; mp3: Uint8Array }
          | { type: 'error'; message: string };
        if (data.type === 'progress') onProgress?.(data.percent);
        else if (data.type === 'done') resolve(data.mp3);
        else reject(new Error(data.message));
      };
      worker.onerror = () => reject(new Error('mp3 encode worker error'));
      // pcm.buffer を transfer（メインスレッド側は以降参照しない）
      worker.postMessage(
        { pcm, sampleRate: TARGET_SAMPLE_RATE, channels: TARGET_CHANNELS, kbps: TARGET_KBPS },
        [pcm.buffer],
      );
    });
    onProgress?.(100);
    // Worker は out.buffer 全体を transfer して返すため buffer をそのまま BlobPart に使える。
    // （TS DOM 型は Uint8Array を SharedArrayBuffer 由来とみなし得るため ArrayBuffer へ明示）
    const blob = new Blob([mp3.buffer as ArrayBuffer], { type: 'audio/mpeg' });
    return new File([blob], rewriteToMp3Filename(file.name), { type: 'audio/mpeg' });
  } finally {
    worker.terminate();
  }
}
